import { useLayoutEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const MAX_ENTRIES = 10;
const entries = new Map();
let restoringScroll = false;

const getScrollTop = () => Math.max(document.body.scrollTop, document.documentElement.scrollTop);
const setScrollTop = top => {
  document.body.scrollTop = top;
  document.documentElement.scrollTop = top;
};

const depsMatch = (a, b) => a.length === b.length && a.every((value, i) => Object.is(value, b[i]));

export const isRestoringListScroll = () => restoringScroll;

export const clearListRestorationEntries = () => entries.clear();

export function useListRestoration(scope, deps = []) {
  const location = useLocation();
  const history = useHistory();
  const entryKey = `${scope}:${location.key || location.pathname + location.search}`;
  const entryKeyRef = useRef(entryKey);
  const restoredRef = useRef(undefined);
  if (restoredRef.current === undefined) {
    const entry = history.action === 'POP' ? entries.get(entryKey) : undefined;
    restoredRef.current = entry && depsMatch(entry.deps, deps) ? entry : null;
  }
  const trackedStateRef = useRef(null);

  useLayoutEffect(() => {
    const restored = restoredRef.current;
    let frame = null;
    if (restored) {
      restoringScroll = true;
      setScrollTop(restored.scrollTop);
      frame = window.requestAnimationFrame(() => {
        setScrollTop(restored.scrollTop);
        frame = window.requestAnimationFrame(() => {
          frame = null;
          restoringScroll = false;
        });
      });
    }
    return () => {
      if (frame != null) {
        window.cancelAnimationFrame(frame);
        restoringScroll = false;
      }
      if (trackedStateRef.current == null) return;
      entries.delete(entryKeyRef.current);
      entries.set(entryKeyRef.current, {
        state: trackedStateRef.current.state,
        deps: trackedStateRef.current.deps,
        scrollTop: getScrollTop()
      });
      if (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value);
    };
  }, []);

  return {
    restoredState: restoredRef.current ? restoredRef.current.state : null,
    trackState: state => {
      if (entryKeyRef.current !== entryKey) {
        entryKeyRef.current = entryKey;
        trackedStateRef.current = null;
      }
      if (state != null) trackedStateRef.current = { state, deps };
    }
  };
}
